import type { EntityExternalLink } from "@sales-ops-amplify/shared";

function normalizeUrl(url: string): string {
  const t = url.trim();
  if (!t) return "";
  if (/^https?:\/\//i.test(t)) return t;
  return `https://${t}`;
}

/** Host + path for display; strips protocol/www and truncates for compact UI. */
function displayUrl(url: string, maxLen = 36): string {
  const raw = url.trim();
  if (!raw) return "";
  let display = raw.replace(/^https?:\/\//i, "").replace(/^www\./i, "");
  if (display.length <= maxLen) return display;
  return `${display.slice(0, Math.max(1, maxLen - 1))}…`;
}

/** Sanitize editor rows into persisted link objects (drops empty rows). */
export function sanitizeExternalLinks(
  links: EntityExternalLink[],
): EntityExternalLink[] {
  return links
    .map((l) => ({
      label: l.label.trim(),
      url: normalizeUrl(l.url),
    }))
    .filter((l) => l.label && l.url);
}

export function ExternalLinksList({
  links,
  emptyLabel = "No external links",
}: {
  links?: EntityExternalLink[] | null;
  emptyLabel?: string;
}) {
  const items = links?.filter((l) => l.label?.trim() && l.url?.trim()) ?? [];
  if (items.length === 0) {
    return <p className="text-sm text-muted-foreground">{emptyLabel}</p>;
  }
  return (
    <ul className="flex flex-wrap gap-2">
      {items.map((l) => {
        const href = normalizeUrl(l.url);
        const shown = displayUrl(l.url);
        return (
          <li key={`${l.label}-${l.url}`} className="min-w-0 max-w-full">
            <a
              href={href}
              target="_blank"
              rel="noreferrer"
              title={href}
              className="group flex max-w-[11.5rem] flex-col rounded-md border border-border bg-muted/20 px-2.5 py-1.5 transition-colors hover:border-brand/40 hover:bg-muted/40"
            >
              <span className="truncate text-xs font-medium text-brand group-hover:underline">
                {l.label}
              </span>
              <span className="truncate text-[10px] text-muted-foreground">
                {shown}
              </span>
            </a>
          </li>
        );
      })}
    </ul>
  );
}

export function ExternalLinksEditor({
  value,
  onChange,
  disabled,
}: {
  value: EntityExternalLink[];
  onChange: (next: EntityExternalLink[]) => void;
  disabled?: boolean;
}) {
  function updateRow(index: number, patch: Partial<EntityExternalLink>) {
    onChange(value.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }

  function removeRow(index: number) {
    onChange(value.filter((_, i) => i !== index));
  }

  function addRow() {
    onChange([...value, { label: "", url: "" }]);
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          External links
        </p>
        <button
          type="button"
          disabled={disabled}
          onClick={addRow}
          className="rounded-md border border-border px-2 py-1 text-[11px] font-medium hover:bg-muted/40 disabled:opacity-50"
        >
          Add link
        </button>
      </div>
      <p className="text-[11px] text-muted-foreground">
        Label + URL pairs for SalesNav, Jira, CRM records, and other tools.
      </p>
      {value.length === 0 ? (
        <p className="text-xs text-muted-foreground">No links yet.</p>
      ) : (
        <ul className="space-y-2">
          {value.map((row, index) => (
            <li
              key={index}
              className="grid gap-2 rounded-md border border-border bg-bg/50 p-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_auto]"
            >
              <input
                type="text"
                value={row.label}
                disabled={disabled}
                placeholder="Label (e.g. SalesNav)"
                onChange={(e) => updateRow(index, { label: e.target.value })}
                className="rounded-md border border-border bg-bg px-2.5 py-1.5 text-sm"
              />
              <div className="min-w-0 space-y-1">
                <input
                  type="url"
                  value={row.url}
                  disabled={disabled}
                  placeholder="https://…"
                  onChange={(e) => updateRow(index, { url: e.target.value })}
                  className="w-full rounded-md border border-border bg-bg px-2.5 py-1.5 text-sm"
                />
                {row.label.trim() && row.url.trim() ? (
                  <a
                    href={normalizeUrl(row.url)}
                    target="_blank"
                    rel="noreferrer"
                    title={normalizeUrl(row.url)}
                    className="inline-block max-w-full truncate text-[11px] font-medium text-brand hover:underline"
                  >
                    Open {row.label.trim()} · {displayUrl(row.url, 28)}
                  </a>
                ) : null}
              </div>
              <button
                type="button"
                disabled={disabled}
                onClick={() => removeRow(index)}
                className="rounded-md px-2 py-1.5 text-xs text-destructive hover:bg-destructive/10 disabled:opacity-50"
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
